import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { SegmentedControl } from '@/components/admin/experiences/theme/segmented-control';
import { ThemeBackgroundPicker } from '@/components/admin/experiences/theme/theme-background-picker';
import {
  BLOCK_ANIMATION_LABEL,
  BUTTON_SHAPE_LABEL,
  BUTTON_STYLE_LABEL,
  FONT_LABEL,
  PAGE_TRANSITION_LABEL,
  SCALE_LABEL,
  SPEED_LABEL,
  THEME_COLORS,
  isOverridden,
  themeInForce,
  veilPercent,
  withToken,
} from '@/lib/experience-theme';
import type {
  AnimationSpeed,
  BlockAnimation,
  ButtonShape,
  ButtonStyle,
  ExperienceFont,
  ExperienceScale,
  ExperienceTheme,
  PageTransitionAnimation,
  ThemeBackgroundKind,
} from '@matter_extractor/shared';
import { type ReactNode, useId } from 'react';

/**
 * Le panneau de la barre de visuel : cinq colonnes, le même dans ses deux états
 * (refonte modulaire §9.4 ; maquette `BarreDeTheme.dc.html` ; issue #57).
 *
 * « Dépliée, la barre est un panneau à cinq colonnes (typographie, couleurs,
 * fond, boutons, mouvement), le même dans ses trois états ; ce qui change est le
 * titre et ce qui est grisé. » Two of the three states live here - the
 * experience's theme and a page's overrides - and the third does not: the
 * block's three tokens are in the inspector's Visuel panel of #58, which is
 * where the mockup itself leaves the question open (« reste à trancher »), and
 * a column drawn for a level nothing mounts would be a column nobody tests.
 *
 * ## Every control writes ONE token, and `undefined` erases it
 *
 * §9.4: « un token absent hérite du niveau au-dessus ». So a control never
 * writes the value it is SHOWING - which is the resolved one - it writes the
 * token, and `withToken` is the single place that knows an erase is a deleted
 * key and not a stored `undefined` (`experience-theme.ts` says what that costs).
 *
 * ## No control here paints in the experience's colours
 *
 * « La barre montre des réglages, pas un rendu : les noms de police y sont dans
 * la police de l'interface, et l'aperçu du bouton est la seule chose peinte dans
 * le thème. » That is why the font selects are plain selects and the swatch is
 * an `<input type="color">`: the rendering happens in the scene next to it,
 * which is the real reader, and a bar that half-rendered would compete with it.
 */

export type ThemeLevel = 'global' | 'page';

export interface ExperienceThemePanelProps {
  readonly level: ThemeLevel;
  /** What THIS level stores: partial tokens, `{}` when it overrides nothing. */
  readonly value: ExperienceTheme;
  /** The level below - the experience's theme at `page`, absent at `global`. */
  readonly inherited?: ExperienceTheme | undefined;
  readonly readOnly: boolean;
  readonly onChange: (next: ExperienceTheme) => void;
}

const COLUMN = 'flex min-w-0 flex-col gap-3';
const GROUP_TITLE = 'font-semibold text-bleu-nuit text-sm leading-5';
const LABEL = 'font-semibold text-gray-600 text-xs uppercase tracking-wide';
const SELECT =
  'h-9 w-full rounded-md border-2 border-gray-300 bg-white px-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-50 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * One row: its label, its control, and - at the page level - where its value
 * comes from.
 *
 * The badge is the whole of what tells the two states apart on screen, so it is
 * rendered by the ROW and never by each control: nineteen controls each drawing
 * their own would be nineteen chances for one of them to forget.
 */
function ThemeField({
  label,
  level,
  overridden,
  htmlFor,
  children,
}: {
  label: string;
  level: ThemeLevel;
  overridden: boolean;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div data-testid={EXPERIENCE_IDS.themeField} data-overridden={overridden} className="space-y-1">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {htmlFor === undefined ? (
          <span className={LABEL}>{label}</span>
        ) : (
          <label htmlFor={htmlFor} className={LABEL}>
            {label}
          </label>
        )}
        {level === 'global' ? null : overridden ? (
          <span className="rounded-md bg-bleu-nuit-tint px-2 py-0.5 font-semibold text-bleu-nuit text-xs leading-4">
            surchargé
          </span>
        ) : (
          <span className="text-gray-500 text-xs leading-4">hérité</span>
        )}
      </div>
      {children}
    </div>
  );
}

export function ExperienceThemePanel({
  level,
  value,
  inherited,
  readOnly,
  onChange,
}: ExperienceThemePanelProps) {
  const ids = useId();
  const force = themeInForce(value, inherited);

  /** One token written at this level; everything below goes through it. */
  function set<Token extends keyof ExperienceTheme>(
    token: Token,
    next: ExperienceTheme[Token] | undefined,
  ): void {
    onChange(withToken(value, token, next));
  }

  const field = (token: keyof ExperienceTheme) => isOverridden(value, token);

  const fontOptions = Object.entries(FONT_LABEL) as [ExperienceFont, string][];
  const scaleOptions = (Object.keys(SCALE_LABEL) as ExperienceScale[]).map((one) => ({
    value: one,
    label: SCALE_LABEL[one],
  }));
  const speedOptions = (Object.keys(SPEED_LABEL) as AnimationSpeed[]).map((one) => ({
    value: one,
    label: SPEED_LABEL[one],
  }));

  return (
    <div
      data-testid={EXPERIENCE_IDS.themePanel}
      className="grid gap-6 border-gray-200 border-t pt-4 lg:grid-cols-3 xl:grid-cols-5"
    >
      {/* ------------------------------------------------------ typographie */}
      <section className={COLUMN}>
        <h3 className={GROUP_TITLE}>Typographie</h3>

        <ThemeField
          label="Police de titre"
          level={level}
          overridden={field('titleFont')}
          htmlFor={`${ids}-titre`}
        >
          <select
            id={`${ids}-titre`}
            className={SELECT}
            disabled={readOnly}
            value={force.titleFont}
            onChange={(event) => set('titleFont', event.currentTarget.value as ExperienceFont)}
          >
            {fontOptions.map(([font, label]) => (
              <option key={font} value={font}>
                {label}
              </option>
            ))}
          </select>
        </ThemeField>

        <ThemeField
          label="Police de texte"
          level={level}
          overridden={field('textFont')}
          htmlFor={`${ids}-texte`}
        >
          <select
            id={`${ids}-texte`}
            className={SELECT}
            disabled={readOnly}
            value={force.textFont}
            onChange={(event) => set('textFont', event.currentTarget.value as ExperienceFont)}
          >
            {fontOptions.map(([font, label]) => (
              <option key={font} value={font}>
                {label}
              </option>
            ))}
          </select>
        </ThemeField>

        <ThemeField label="Échelle" level={level} overridden={field('textScale')}>
          <SegmentedControl
            label="Échelle du texte"
            options={scaleOptions}
            value={force.textScale}
            disabled={readOnly}
            onChange={(next) => set('textScale', next)}
          />
        </ThemeField>

        <p className="text-gray-600 text-xs leading-4">
          Cinq polices embarquées : rien n’est chargé depuis l’écran de la personne.
        </p>
      </section>

      {/* ---------------------------------------------------------- couleurs */}
      <section className={COLUMN}>
        <h3 className={GROUP_TITLE}>Couleurs</h3>
        {THEME_COLORS.map(({ token, label }) => (
          <ThemeField
            key={token}
            label={label}
            level={level}
            overridden={field(token)}
            htmlFor={`${ids}-${token}`}
          >
            <div className="flex items-center gap-2">
              <input
                id={`${ids}-${token}`}
                type="color"
                disabled={readOnly}
                value={force[token]}
                onChange={(event) => set(token, event.currentTarget.value)}
                className="size-8 shrink-0 cursor-pointer rounded-md border-2 border-gray-300 bg-white p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
              />
              <span className="font-mono text-gray-700 text-xs leading-4">{force[token]}</span>
            </div>
          </ThemeField>
        ))}
      </section>

      {/* -------------------------------------------------------------- fond */}
      <section className={COLUMN}>
        <h3 className={GROUP_TITLE}>Fond</h3>

        <ThemeField label="Nature" level={level} overridden={field('backgroundKind')}>
          <SegmentedControl<ThemeBackgroundKind>
            label="Nature du fond"
            options={[
              { value: 'color', label: 'Couleur' },
              { value: 'image', label: 'Image' },
            ]}
            value={force.backgroundKind}
            disabled={readOnly}
            onChange={(next) => set('backgroundKind', next)}
          />
        </ThemeField>

        {force.backgroundKind === 'image' ? (
          <ThemeField
            label="Image de la médiathèque"
            level={level}
            overridden={field('backgroundImageAssetId')}
          >
            <ThemeBackgroundPicker
              assetId={force.backgroundImageAssetId}
              readOnly={readOnly}
              onPick={(next) => set('backgroundImageAssetId', next)}
            />
          </ThemeField>
        ) : null}

        <ThemeField
          label="Opacité du voile"
          level={level}
          overridden={field('backgroundVeil')}
          htmlFor={`${ids}-voile`}
        >
          <div className="flex items-center gap-2">
            <input
              id={`${ids}-voile`}
              type="range"
              min={0}
              max={100}
              step={5}
              disabled={readOnly}
              value={veilPercent(force.backgroundVeil)}
              onChange={(event) => set('backgroundVeil', Number(event.currentTarget.value) / 100)}
              className="min-w-0 flex-1 accent-bleu-nuit"
            />
            <span className="shrink-0 text-gray-700 text-xs tabular-nums leading-4">
              {veilPercent(force.backgroundVeil)} %
            </span>
          </div>
        </ThemeField>

        <p className="text-gray-600 text-xs leading-4">
          Le voile assombrit une image pour garder le texte lisible.
        </p>
      </section>

      {/* ----------------------------------------------------------- boutons */}
      <section className={COLUMN}>
        <h3 className={GROUP_TITLE}>Boutons</h3>

        <ThemeField label="Forme" level={level} overridden={field('buttonShape')}>
          <SegmentedControl<ButtonShape>
            label="Forme des boutons"
            options={(Object.keys(BUTTON_SHAPE_LABEL) as ButtonShape[]).map((one) => ({
              value: one,
              label: BUTTON_SHAPE_LABEL[one],
            }))}
            value={force.buttonShape}
            disabled={readOnly}
            onChange={(next) => set('buttonShape', next)}
          />
        </ThemeField>

        <ThemeField label="Taille" level={level} overridden={field('buttonSize')}>
          <SegmentedControl
            label="Taille des boutons"
            options={scaleOptions}
            value={force.buttonSize}
            disabled={readOnly}
            onChange={(next) => set('buttonSize', next)}
          />
        </ThemeField>

        <ThemeField label="Style" level={level} overridden={field('buttonStyle')}>
          <SegmentedControl<ButtonStyle>
            label="Style des boutons"
            options={(Object.keys(BUTTON_STYLE_LABEL) as ButtonStyle[]).map((one) => ({
              value: one,
              label: BUTTON_STYLE_LABEL[one],
            }))}
            value={force.buttonStyle}
            disabled={readOnly}
            onChange={(next) => set('buttonStyle', next)}
          />
        </ThemeField>
      </section>

      {/* --------------------------------------------------------- mouvement */}
      <section className={COLUMN}>
        <h3 className={GROUP_TITLE}>Mouvement</h3>

        <ThemeField
          label="Animation d’entrée par défaut"
          level={level}
          overridden={field('blockAnimation')}
          htmlFor={`${ids}-entree`}
        >
          <select
            id={`${ids}-entree`}
            className={SELECT}
            disabled={readOnly}
            value={force.blockAnimation}
            onChange={(event) => set('blockAnimation', event.currentTarget.value as BlockAnimation)}
          >
            {(Object.keys(BLOCK_ANIMATION_LABEL) as BlockAnimation[]).map((one) => (
              <option key={one} value={one}>
                {BLOCK_ANIMATION_LABEL[one]}
              </option>
            ))}
          </select>
        </ThemeField>

        <ThemeField label="Rapidité de l’entrée" level={level} overridden={field('blockSpeed')}>
          <SegmentedControl
            label="Rapidité de l’animation d’entrée"
            options={speedOptions}
            value={force.blockSpeed}
            disabled={readOnly}
            onChange={(next) => set('blockSpeed', next)}
          />
        </ThemeField>

        <ThemeField
          label="Transition entre pages"
          level={level}
          overridden={field('pageTransition')}
          htmlFor={`${ids}-transition`}
        >
          <select
            id={`${ids}-transition`}
            className={SELECT}
            disabled={readOnly}
            value={force.pageTransition}
            onChange={(event) =>
              set('pageTransition', event.currentTarget.value as PageTransitionAnimation)
            }
          >
            {(Object.keys(PAGE_TRANSITION_LABEL) as PageTransitionAnimation[]).map((one) => (
              <option key={one} value={one}>
                {PAGE_TRANSITION_LABEL[one]}
              </option>
            ))}
          </select>
        </ThemeField>

        <ThemeField
          label="Rapidité de la transition"
          level={level}
          overridden={field('pageTransitionSpeed')}
        >
          <SegmentedControl
            label="Rapidité de la transition entre pages"
            options={speedOptions}
            value={force.pageTransitionSpeed}
            disabled={readOnly}
            onChange={(next) => set('pageTransitionSpeed', next)}
          />
        </ThemeField>

        <ThemeField label="Réduire les animations" level={level} overridden={field('reduceMotion')}>
          <label className="flex items-start gap-2 text-gray-800 text-sm leading-5">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={force.reduceMotion}
              onChange={(event) => set('reduceMotion', event.currentTarget.checked)}
              className="mt-0.5 size-4 shrink-0 accent-bleu-nuit"
            />
            <span>Réduire les animations si la personne l’a demandé à son système</span>
          </label>
        </ThemeField>
      </section>
    </div>
  );
}
